import { typeMeta, BLOCK_TYPES } from "@/lib/forms";
import { ChevronUp, ChevronDown, Trash2, Copy, Plus, Flag, GitBranch } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function AddBlockMenu({ onAdd, label = "Add block", exclude = [], testid = "add-block-btn" }) {
  const groups = [...new Set(BLOCK_TYPES.map((b) => b.group))];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="btn w-full justify-center" data-testid={testid}><Plus size={13} />{label}</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 max-h-[70vh] overflow-auto">
        {groups.map((g, gi) => (
          <div key={g}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-neutral-500">{g}</DropdownMenuLabel>
            {BLOCK_TYPES.filter((b) => b.group === g && !exclude.includes(b.type)).map((b) => (
              <DropdownMenuItem key={b.type} onClick={() => onAdd(b.type)} data-testid={`add-type-${b.type}`}>
                <b.icon size={13} className="mr-2" />{b.label}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Row({ b, n, selected, onSelect, act, child }) {
  const M = typeMeta(b.type);
  return (
    <div onClick={() => onSelect(b.id)} data-testid={`block-row-${b.id}`}
      className={`group flex items-center gap-2 px-2 py-2 rounded cursor-pointer transition-colors ${child ? "ml-5" : ""} ${selected ? "bg-[var(--panel-2)] ring-1 ring-[var(--border-strong)]" : "hover:bg-[var(--panel-2)]"}`}>
      <span className="text-[10px] font-mono text-neutral-600 w-5 text-right">{n}</span>
      <M.icon size={13} className="text-neutral-400 shrink-0" />
      <span className="flex-1 truncate text-[13px]">{b.label || <span className="text-neutral-600">{M.label}</span>}</span>
      {b.logic?.rules?.length > 0 && <GitBranch size={11} className="text-[var(--ai)]" title="Has logic" />}
      {b.required && <span className="text-[var(--warn)] text-xs">*</span>}
      <span className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
        <button className="btn-ghost p-0.5" onClick={(e) => act(e, "up", b.id)} data-testid={`block-up-${b.id}`}><ChevronUp size={13} /></button>
        <button className="btn-ghost p-0.5" onClick={(e) => act(e, "down", b.id)} data-testid={`block-down-${b.id}`}><ChevronDown size={13} /></button>
        <button className="btn-ghost p-0.5" onClick={(e) => act(e, "dup", b.id)} data-testid={`block-dup-${b.id}`}><Copy size={12} /></button>
        <button className="btn-ghost p-0.5 hover:text-[var(--breach)]" onClick={(e) => act(e, "del", b.id)} data-testid={`block-del-${b.id}`}><Trash2 size={12} /></button>
      </span>
    </div>
  );
}

export default function BlockList({ blocks, selectedId, onSelect, onAction, onAdd, onAddChild, endings = [], onAddEnding }) {
  const act = (e, kind, id) => { e.stopPropagation(); onAction(kind, id); };
  let n = 0;
  return (
    <div className="space-y-0.5" data-testid="block-list">
      {blocks.map((b) => (
        <div key={b.id}>
          <Row b={b} n={b.type === "group" ? "§" : ++n} selected={selectedId === b.id} onSelect={onSelect} act={act} />
          {b.type === "group" && (
            <div className="space-y-0.5 border-l border-[var(--border)] ml-4 pl-1 my-1">
              {(b.children || []).map((c) => (
                <Row key={c.id} b={c} n={++n} child selected={selectedId === c.id} onSelect={onSelect} act={act} />
              ))}
              <div className="ml-5 pr-2 py-1">
                <AddBlockMenu label="Add to group" exclude={["group", "welcome", "statement", "calendar_meet", "calendlo"]}
                  onAdd={(t) => onAddChild(b.id, t)} testid={`add-to-group-${b.id}`} />
              </div>
            </div>
          )}
        </div>
      ))}
      <div className="pt-2"><AddBlockMenu onAdd={onAdd} /></div>
      <div className="pt-4 mt-2 border-t border-[var(--border)]" data-testid="endings-list">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 px-2 mb-1">Endings</div>
        {endings.map((e) => (
          <div key={e.id} onClick={() => onSelect(e.id)} data-testid={`ending-row-${e.id}`}
            className={`flex items-center gap-2 px-2 py-2 rounded cursor-pointer transition-colors ${selectedId === e.id ? "bg-[var(--panel-2)] ring-1 ring-[var(--border-strong)]" : "hover:bg-[var(--panel-2)]"}`}>
            <Flag size={13} className={e.outcome === "disqualified" ? "text-[var(--breach)]" : e.outcome === "qualified" ? "text-[var(--ok)]" : "text-neutral-400"} />
            <span className="flex-1 truncate text-[13px]">{e.title || "Ending"}</span>
            <span className="text-[10px] text-neutral-600">{e.outcome}</span>
          </div>
        ))}
        <button className="btn w-full justify-center mt-1" onClick={onAddEnding} data-testid="add-ending-btn"><Plus size={13} />Add ending</button>
      </div>
    </div>
  );
}
